import { Link } from "react-router-dom";

import { agentDisplayName } from "@/lib/agent-display-name";
import { cn } from "@/lib/utils";
import { NvBadge } from "./nv-badge";
import { NvBookmarkStarIcon, NvTrendIcon } from "./nv-icons-finder";
import { NvStarIcon } from "./nv-icons";
import { NvInitialTile } from "./nv-initial-tile";
import { NvPill } from "./nv-pill";
import { NvTagPill, type NvTagValue } from "./nv-tag-pill";
import type { NvAgentMetric } from "./nv-agent-cards";

// Agent Finder 결과 카드 (Figma ver.7 "Card / Agent Result (제안)" 847:49745, 10/13 제안 09 · 13 실데이터 규칙):
//   로고 56 박스(안 40, contain · 없으면 이니셜 타일) · 제품명 1줄 말줄임(전체명은 마우스 오버) · 공급사명 ·
//   태그(유형 · 도입 형태) + 산업/업무 경로 한 줄 · 설명 2줄 · 대표 지표 띠(라벨 한 줄 고정, 값 14 굵게 1줄, 없으면 띠 숨김) · 북마크 별.
// 공용 NvAgentResultCard 는 그대로 두고 이 화면 전용으로 새로 만들었어요.
export interface NvFinderResultCardProps {
  name: string;
  supplier?: string | null;
  logoUrl?: string;
  // 이니셜 타일 색을 고르는 키 (에이전트 id)
  toneKey?: string;
  certified?: boolean;
  // 컨셉추얼 구분 뱃지 (IA 65, PwC 내부에게만 보이는 에이전트)
  conceptual?: boolean;
  topMatch?: boolean;
  tags?: (string | NvTagValue)[];
  // e.g. "중공업·EPC · 조선·해양"
  category?: string;
  summary: string;
  metric?: NvAgentMetric | null;
  bookmarked?: boolean;
  onBookmarkToggle?: (next: boolean) => void;
  to?: string;
  linkState?: unknown;
  // △(비회원 · 승인 대기 · 공급자) Blur 미리보기: 링크 · 북마크 비활성
  locked?: boolean;
  className?: string;
}

function LogoBox({ name, logoUrl, toneKey }: { name: string; logoUrl?: string; toneKey?: string }) {
  return (
    <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-[12px] border border-nv-bg-muted bg-nv-surface">
      {logoUrl ? <img src={logoUrl} alt="" className="h-10 w-10 object-contain" /> : <NvInitialTile name={name} toneKey={toneKey} variant="card" />}
    </span>
  );
}

function MetricStrip({ metric }: { metric: NvAgentMetric }) {
  return (
    <div className="flex items-center gap-[10px] rounded-nv-chip bg-[#F0F9F4] px-3 py-2" title={`${metric.label} | ${metric.value}`}>
      <NvTrendIcon className="shrink-0 text-[#15803D]" />
      <span className="shrink-0 whitespace-nowrap text-[13px] font-medium leading-4 text-[#15803D]">{metric.label}</span>
      <span aria-hidden className="shrink-0 text-[12px] leading-[14px] text-[#B7DFC6]">
        |
      </span>
      <span className="min-w-0 truncate text-[14px] font-bold leading-5 text-[#166534]">{metric.value}</span>
    </div>
  );
}

function BookmarkStar({ bookmarked, onToggle, disabled }: { bookmarked: boolean; onToggle?: (next: boolean) => void; disabled?: boolean }) {
  // 기본: 회색 외곽선 · 마우스 오버: 회색 원 배경 · 북마크됨: 주황 채움 · 오버: 연한 주황 원 배경 (제안 "북마크 아이콘 상태")
  const label = bookmarked ? "북마크 해제" : "북마크에 추가";
  return (
    <button
      type="button"
      aria-pressed={bookmarked}
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={() => onToggle?.(!bookmarked)}
      className="group absolute right-2 top-[22px] z-10 flex h-[22px] w-[22px] items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nv-brand"
    >
      <span
        aria-hidden
        className={cn("absolute -inset-[9px] rounded-full transition-colors", !disabled && (bookmarked ? "group-hover:bg-nv-brand-subtle" : "group-hover:bg-nv-bg-subtle"))}
      />
      <NvBookmarkStarIcon filled={bookmarked} className="relative" />
    </button>
  );
}

function NvFinderResultCard({
  name,
  supplier,
  logoUrl,
  toneKey,
  certified = false,
  conceptual = false,
  topMatch = false,
  tags = [],
  category,
  summary,
  metric,
  bookmarked,
  onBookmarkToggle,
  to,
  linkState,
  locked = false,
  className,
}: NvFinderResultCardProps) {
  const displayName = agentDisplayName(name);
  const titleClass = "min-w-0 truncate text-[18px] font-bold leading-[26px] text-nv-text-primary";
  return (
    <div className={cn("relative flex gap-5 rounded-[14px] border border-nv-border bg-nv-surface px-6 py-[22px]", className)}>
      <LogoBox name={name} logoUrl={logoUrl} toneKey={toneKey} />

      {/* 오른쪽 26은 북마크 별 자리 — 글 폭이 시안(644)과 같아져 줄바꿈 위치가 같아요 */}
      <div className="flex min-w-0 flex-1 flex-col gap-2 pr-[26px]">
        <div className="flex min-w-0 items-center gap-2">
          {to && !locked ? (
            // 전체 이름은 title(마우스 오버). 링크가 카드 전체를 덮어요(after) — NvCardLink 와 같은 방식
            <Link to={to} state={linkState} title={name} className={cn(titleClass, "after:absolute after:inset-0 after:content-['']")}>
              {displayName}
            </Link>
          ) : (
            <span title={name} className={titleClass}>
              {displayName}
            </span>
          )}
          {certified && <NvBadge className="shrink-0">PwC Certified</NvBadge>}
          {conceptual && <NvPill className="shrink-0">컨셉추얼</NvPill>}
          {topMatch && (
            <NvPill tone="highlight" className="shrink-0 gap-1 border border-nv-brand">
              <NvStarIcon size={13} />
              상위 일치
            </NvPill>
          )}
        </div>

        {supplier && <p className="truncate text-[12px] font-medium leading-[14px] text-[#6B6F77]">{supplier}</p>}

        {(tags.length > 0 || category) && (
          <div className="flex min-w-0 items-center gap-2">
            {tags.map((tag) => {
              const value = typeof tag === "string" ? { label: tag } : tag;
              return (
                <NvTagPill key={value.label} label={value.label} definition={value.definition} className="shrink-0 bg-nv-bg-subtle px-2 py-[3px] text-[12px] leading-[14px]" />
              );
            })}
            {category && (
              <>
                {tags.length > 0 && (
                  <span aria-hidden className="shrink-0 text-[12px] leading-[14px] text-[#CBD1D6]">
                    ·
                  </span>
                )}
                <span className="min-w-0 truncate text-[13px] leading-4 text-nv-text-tertiary" title={category}>
                  {category}
                </span>
              </>
            )}
          </div>
        )}

        <p className="line-clamp-2 text-[15px] leading-6 text-nv-text-primary">{summary}</p>

        {metric && <MetricStrip metric={metric} />}
      </div>

      {bookmarked !== undefined && <BookmarkStar bookmarked={bookmarked} onToggle={onBookmarkToggle} disabled={locked} />}
    </div>
  );
}

// 불러오는 중 (Figma "Skeleton" 796x180): 카드 구조 그대로 — 로고 56 · 제목 · 태그 3 · 설명 2줄 · 지표 띠
function NvFinderCardSkeleton({ className }: { className?: string }) {
  const bar = "block animate-pulse bg-[#F0F1F3]";
  return (
    <div aria-hidden className={cn("flex h-[180px] gap-5 rounded-nv-card border border-nv-border bg-nv-surface px-6 py-[22px]", className)}>
      <span className={cn(bar, "h-14 w-14 shrink-0 rounded-[12px]")} />
      <span className="flex min-w-0 flex-1 flex-col gap-[10px]">
        <span className={cn(bar, "h-[18px] w-[240px] max-w-full rounded-[4px]")} />
        <span className="flex gap-[6px]">
          <span className={cn(bar, "h-5 w-16 rounded-[4px]")} />
          <span className={cn(bar, "h-5 w-[78px] rounded-[4px]")} />
          <span className={cn(bar, "h-5 w-[110px] rounded-[4px]")} />
        </span>
        <span className={cn(bar, "h-3 w-[600px] max-w-full rounded-[4px]")} />
        <span className={cn(bar, "h-3 w-[440px] max-w-full rounded-[4px]")} />
        <span className={cn(bar, "h-8 w-[600px] max-w-full rounded-nv-chip")} />
      </span>
    </div>
  );
}

export { NvFinderResultCard, NvFinderCardSkeleton };
